import BaseController from "sap/mcsuite/controller/BaseController";
import Dialog, {
	Dialog$BeforeOpenEvent,
	Dialog$AfterCloseEvent,
} from "sap/m/Dialog";

export default class DialogUtils {
	private static dialogHierarchy: Dialog[] = [];

    /**
     * Opens a dialog with the specified ID and attaches event handlers for after open and after close events.
     * The dialog is added to the controller's dependents to ensure proper cleanup.
     *
     * @param {BaseController} controller - The controller that manages this dialog.
     * @param {string} dialogId - The ID of the dialog to be opened.
     * @param {function} beforeOpen - Callback function to be executed after the dialog is opened.
     * @param {function} afterClose - Callback function to be executed after the dialog is closed.
     */
	public static async openDialog(
		controller: BaseController,
		dialogId: string,
		beforeOpen?: (event: Dialog$BeforeOpenEvent) => void,
		afterClose?: (event: Dialog$AfterCloseEvent) => void,
	): Promise<void> {
		const dialog = (await controller.loadFragment({
			name: dialogId,
			addToDependents: true,
		})) as Dialog;
		if (!dialog) {
			console.error(`Dialog with ID ${dialogId} not found.`);
			return;
		}
		if (beforeOpen) dialog.attachBeforeOpen(beforeOpen.bind(controller));
		dialog.attachAfterOpen(() => {
			DialogUtils.dialogHierarchy.push(dialog);
		});
		if (afterClose) dialog.attachAfterClose(afterClose.bind(controller));
		dialog.attachAfterClose(() => {
			const index = DialogUtils.dialogHierarchy.indexOf(dialog);
			if (index > -1) {
				DialogUtils.dialogHierarchy.splice(index, 1);
			}
			dialog.destroy();
		});

		DialogUtils.closeOnOutsideClick(dialog);
		dialog.open();
	}

	private static closeOnOutsideClick(dialog: Dialog): void {
		dialog.attachAfterOpen(() => {
			document.addEventListener("click", (event) => {
				const isTopDialog =
					DialogUtils.dialogHierarchy[
						DialogUtils.dialogHierarchy.length - 1
					] === dialog;
				const domRef = dialog.getDomRef();
				if (!domRef) {
					dialog.close();
					return;
				}
				const isClickInsideDialog = dialog
					.getDomRef()
					.contains(event.target as Node);

				// Dynamically check if any visible Popover is open within the dialog
				const popovers: HTMLElement[] = Array.from(
					document.querySelectorAll(".sapMPopover")
				);
				const isPopoverOpen = popovers.some((popover) => {
					const isInsideDialog = dialog.getDomRef().contains(popover);
					const isVisible =
						popover.offsetParent !== null &&
						window.getComputedStyle(popover).visibility !== "hidden";
					return isInsideDialog && isVisible;
				});

				if (isTopDialog && !isClickInsideDialog && !isPopoverOpen) {
					dialog.close();
				}
			});
		});
	}
}
